import { useAuth } from "@/context/AuthContext";
import { ScanButton } from "@/components/ScanButton";
import { DriverToolsBar } from "@/components/DriverToolsBar";
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Trash2, FlaskConical, Upload, FileText, X } from "lucide-react";
import { ViolationsTab, PspMonitoringControl } from "@/components/Violations";
import { ageOf } from "@/components/DriverGroupRow";
import { ClearinghousePanel } from "@/components/drivers/ClearinghousePanel";
import { PspConsentPanel } from "@/components/drivers/PspConsentPanel";
import { EmployerInquiriesPanel } from "@/components/drivers/EmployerInquiriesPanel";

import { SsnValue, TEST_TYPES, statusBadge, expiryBadge, Info } from "@/components/drivers/driverUi";
import { restrictionText, ENDORSEMENT_LABEL } from "@/components/drivers/Restrictions";

function HireRecordRow({ rec }) {
  const when = rec.at ? new Date(rec.at).toLocaleString([], { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "";
  const over = rec.overridden || [];
  return (
    <div className="col-span-2 rounded-md border border-slate-200 bg-slate-50 p-3" data-testid="hire-record">
      <div className="text-xs text-slate-500 uppercase tracking-wide mb-1">Hire decision · 391.21 / 391.23</div>
      <div className="text-sm text-slate-900"><b>{rec.decision === "hired" ? "Hired" : "Not hired"}</b> by {rec.by} · {when}</div>
      {over.length > 0 ? (
        <div className="mt-1.5 text-xs text-amber-800" data-testid="hire-record-overridden"><b>Hired with {over.length} open checklist item{over.length === 1 ? "" : "s"}:</b> {over.join(", ")}</div>
      ) : (
        <div className="mt-1.5 text-xs text-emerald-700" data-testid="hire-record-complete">All checklist items were complete at hire.</div>
      )}
      <ul className="mt-2 grid sm:grid-cols-2 gap-x-4 gap-y-0.5 text-xs" data-testid="hire-record-items">
        {(rec.checklist || []).map((i) => <li key={i.key} className={i.done ? "text-slate-600" : "text-amber-800"}>{i.done ? "✓" : "○"} {i.label}{i.manual ? <span className="text-slate-400 italic"> · manual</span> : null}</li>)}
      </ul>
    </div>
  );
}

function RenewalToggle({ driver, field, onChanged }) {
  const on = !!driver.renewals?.[field];
  const days = driver[field] ? Math.round((new Date(driver[field]) - new Date().setHours(0, 0, 0, 0)) / 86400000) : null;
  if (days === null || days > 30) return null;
  const flip = async (v) => {
    try {
      const { data } = await api.patch(`/drivers/${driver.id}`, { renewals: { ...(driver.renewals || {}), [field]: v } });
      toast.success(v ? "Marked as renewal in progress — alerts paused" : "Renewal flag cleared"); onChanged(data);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <label className="mt-1 flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
      <Switch checked={on} onCheckedChange={flip} className="scale-75" data-testid={`renewal-toggle-${field}`} />
      {on ? <span className="text-sky-700 font-medium">Renewal in progress</span> : "Renewal in progress?"}
    </label>
  );
}

export function DriverDetailDialog({ driver: initial, onClose, initialTab = "info" }) {
  const isAdmin = useAuth().user?.role === "admin";
  const [driver, setDriver] = useState(initial);
  useEffect(() => { setDriver(initial); }, [initial]);
  const [tests, setTests] = useState([]);
  const [certs, setCerts] = useState([]);
  const [testOpen, setTestOpen] = useState(false);
  const [testForm, setTestForm] = useState({ test_type: "random", test_category: "drug", result: "pending", test_date: "", collection_site: "", mro_notes: "" });

  const driverId = driver?.id;
  useEffect(() => {
    if (!driverId) return;
    let cancelled = false;
    (async () => {
      try {
        const [{ data: t }, { data: c }] = await Promise.all([
          api.get(`/tests?driver_id=${driverId}`),
          api.get(`/certificates`),
        ]);
        if (cancelled) return;
        setTests(t);
        setCerts(c.filter((x) => x.driver_id === driverId));
      } catch (err) { if (!cancelled) toast.error(formatApiError(err)); }
    })();
    return () => { cancelled = true; };
  }, [driverId]);

  if (!driver) return null;

  const submitTest = async (e) => {
    e.preventDefault();
    try {
      await api.post("/tests", { ...testForm, driver_id: driver.id });
      toast.success("Test recorded");
      const { data: t } = await api.get(`/tests?driver_id=${driver.id}`);
      setTests(t);
      setTestOpen(false);
      setTestForm({ test_type: "random", test_category: "drug", result: "pending", test_date: "", collection_site: "", mro_notes: "" });
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <Dialog open={!!driver} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="text-2xl">{driver.first_name} {driver.last_name}</DialogTitle>
          <div className="text-sm text-slate-500 font-mono flex flex-wrap gap-x-3 gap-y-1">
            <span>{driver.is_cdl === false ? "License" : `CDL${driver.cdl_class ? ` Class ${driver.cdl_class}` : ""}`} {driver.license_number} · {driver.license_state}{driver.is_cdl !== false && driver.endorsements?.length ? <span className="ml-1 text-blue-700" data-testid="driver-header-endorsements">· {driver.endorsements.join(" ")}</span> : null}</span>
            <span className="text-slate-700" data-testid="driver-header-dob">DOB {driver.dob ? `${driver.dob}${ageOf(driver.dob) != null ? ` (${ageOf(driver.dob)})` : ""}` : "—"}</span>
          </div>
        </DialogHeader>
        <Tabs defaultValue={initialTab} key={initialTab} className="mt-2">
          <TabsList>
            <TabsTrigger value="info" data-testid="driver-tab-info">Overview</TabsTrigger>
            <TabsTrigger value="tests" data-testid="driver-tab-tests">DOT Tests ({tests.length})</TabsTrigger>
            <TabsTrigger value="certs" data-testid="driver-tab-certs">Certificates ({certs.length + (driver.report_documents?.length || 0)})</TabsTrigger>
            <TabsTrigger value="violations" data-testid="driver-tab-violations">Violations</TabsTrigger>
            <TabsTrigger value="clearinghouse" data-testid="driver-tab-clearinghouse">Clearinghouse</TabsTrigger>
          </TabsList>
          <TabsContent value="clearinghouse" className="mt-4"><ClearinghousePanel driver={driver} isAdmin={isAdmin} /></TabsContent>
          <TabsContent value="violations" className="mt-4"><ViolationsTab driverId={driver.id} isAdmin={isAdmin} /></TabsContent>
          <TabsContent value="info" className="mt-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <DriverToolsBar driver={driver} onChanged={setDriver} />
              <Info l="Status" v={driver.status} />
              <Info l="In Random Pool" v={driver.in_random_pool ? "Yes" : "No"} />
              <Info l="Hire Date" v={driver.hire_date || "—"} />
              <Info l="DOB" v={driver.dob || "—"} />
              <Info l="SSN" v={<SsnValue ssn={driver.ssn} driverId={driver.id} isAdmin={isAdmin} />} />
              <Info l={driver.is_cdl === false ? "License Expiry" : "CDL Expiry"} v={<>{expiryBadge(driver.cdl_expiry, driver.renewals?.cdl_expiry)}<RenewalToggle driver={driver} field="cdl_expiry" onChanged={setDriver} /></>} />
              <Info l="Medical Card Expiry" v={<>{expiryBadge(driver.medical_card_expiry, driver.renewals?.medical_card_expiry)}<RenewalToggle driver={driver} field="medical_card_expiry" onChanged={setDriver} /></>} />
              <Info l="Phone" v={driver.phone || "—"} />
              <Info l="Email" v={driver.email || "—"} />
              <Info l="Current Address" v={driver.address?.street || driver.address?.city ? `${driver.address.street || ""}${driver.address.street ? ", " : ""}${[driver.address.city, driver.address.county && `${driver.address.county} County`, driver.address.state].filter(Boolean).join(", ")} ${driver.address.zip || ""}`.trim() : <span className="text-amber-700">Missing — needed for background checks</span>} />
              <Info l="DOT Number" v={driver.dot_number || "—"} />
              <Info l="Hazmat endorsement" v={["H", "X"].includes(driver.hazmat_endorsement) ? `${driver.hazmat_endorsement} · TSA ${driver.tsa_sta_expiry ? `expires ${driver.tsa_sta_expiry}` : "date missing"}` : "None"} />
              {driver.is_cdl !== false && <Info l="CDL class / endorsements" v={<span data-testid="driver-class-endorsements-value">{driver.cdl_class ? `Class ${driver.cdl_class}` : "Class —"}{" · "}{driver.endorsements?.length ? driver.endorsements.map((e) => `${e} ${ENDORSEMENT_LABEL[e] || ""}`.trim()).join(", ") : "No endorsements"}</span>} />}
              {driver.is_cdl !== false && <Info l="CDL restrictions" v={<span data-testid="driver-restrictions-value">{restrictionText(driver)}</span>} span />}
              {["H", "X"].includes(driver.hazmat_endorsement) && <TsaLetterRow driver={driver} onChanged={setDriver} />}
              {["H", "X"].includes(driver.hazmat_endorsement) && <TrainingCertRow driver={driver} onChanged={setDriver} />}
              <Info l="Notes" v={driver.notes || "—"} span />
              {driver.hire_record && <HireRecordRow rec={driver.hire_record} />}
              <PspMonitoringControl driver={driver} onChanged={setDriver} />
            </div>
          </TabsContent>
          <TabsContent value="tests" className="mt-4">
            <div className="flex justify-end mb-3">
              <Button size="sm" onClick={() => setTestOpen(true)} data-testid="add-test-button"><FlaskConical className="h-4 w-4 mr-2" />Record Test</Button>
            </div>
            <Table>
              <TableHeader>
                <TableRow><TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead>Category</TableHead><TableHead>Result</TableHead><TableHead>Site</TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {tests.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-6 text-slate-500">No tests recorded.</TableCell></TableRow>}
                {tests.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell className="font-mono text-xs">{t.test_date}</TableCell>
                    <TableCell className="capitalize">{t.test_type.replaceAll("-", " ")}</TableCell>
                    <TableCell className="capitalize">{t.test_category}</TableCell>
                    <TableCell>{statusBadge(t.result)}</TableCell>
                    <TableCell className="text-xs text-slate-500">{t.collection_site || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Dialog open={testOpen} onOpenChange={setTestOpen}>
              <DialogContent>
                <DialogHeader><DialogTitle>Record DOT Test</DialogTitle></DialogHeader>
                <form onSubmit={submitTest} className="grid grid-cols-2 gap-3">
                  <div className="col-span-2"><Label>Test Type</Label>
                    <Select value={testForm.test_type} onValueChange={(v) => setTestForm({ ...testForm, test_type: v })}>
                      <SelectTrigger data-testid="test-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{TEST_TYPES.map((t) => (<SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>))}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Category</Label>
                    <Select value={testForm.test_category} onValueChange={(v) => setTestForm({ ...testForm, test_category: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="drug">Drug</SelectItem>
                        <SelectItem value="alcohol">Alcohol</SelectItem>
                        <SelectItem value="both">Both</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Result</Label>
                    <Select value={testForm.result} onValueChange={(v) => setTestForm({ ...testForm, result: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="negative">Negative</SelectItem>
                        <SelectItem value="positive">Positive</SelectItem>
                        <SelectItem value="refusal">Refusal</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2"><Label>Test Date</Label><Input type="date" required value={testForm.test_date} onChange={(e) => setTestForm({ ...testForm, test_date: e.target.value })} data-testid="test-date-input" /></div>
                  <div className="col-span-2"><Label>Collection Site</Label><Input value={testForm.collection_site} onChange={(e) => setTestForm({ ...testForm, collection_site: e.target.value })} /></div>
                  <div className="col-span-2"><Label>MRO Notes</Label><Textarea rows={2} value={testForm.mro_notes} onChange={(e) => setTestForm({ ...testForm, mro_notes: e.target.value })} /></div>
                  <DialogFooter className="col-span-2"><Button className="bg-blue-700 hover:bg-blue-800" type="submit" data-testid="test-save-button">Save Test</Button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </TabsContent>
          <TabsContent value="certs" className="mt-4">
            <div className="mb-4"><PspConsentPanel driver={driver} /></div>
            <div className="mb-4"><EmployerInquiriesPanel driver={driver} /></div>
            <ReportDocsSection driver={driver} isAdmin={isAdmin} onChanged={setDriver} />
            {certs.length === 0 ? (
              <div className="text-sm text-slate-500 py-6 text-center">No certificates uploaded for this driver.</div>
            ) : (
              <Table>
                <TableHeader><TableRow><TableHead>Type</TableHead><TableHead>Issued</TableHead><TableHead>Expiry</TableHead><TableHead>File</TableHead></TableRow></TableHeader>
                <TableBody>
                  {certs.map((c) => (
                    <TableRow key={c.id}><TableCell>{c.cert_type}</TableCell><TableCell className="font-mono text-xs">{c.issued_date || "—"}</TableCell><TableCell>{c.no_expiration ? <span className="text-xs text-slate-600">No expiration</span> : expiryBadge(c.expiry_date)}</TableCell>
                      <TableCell>{c.file_path ? (
                        <button type="button" className="text-blue-700 underline text-sm" data-testid={`driver-cert-download-${c.id}`} onClick={async () => {
                          try {
                            const res = await api.get(`/certificates/${c.id}/file`, { responseType: "blob" });
                            const url = URL.createObjectURL(res.data); window.open(url, "_blank", "noopener");
                          } catch (err) { toast.error(formatApiError(err)); }
                        }}>{c.file_name || "Download"}</button>
                      ) : <span className="text-slate-400">—</span>}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

function ReportDocsSection({ driver, isAdmin, onChanged }) {
  const docs = [...(driver.report_documents || [])].sort((a, b) => (b.uploaded_at || "").localeCompare(a.uploaded_at || ""));
  if (!docs.length) return null;
  const open = async (d) => {
    try { const res = await api.get(`/reports/${d.report_id}/files/${d.file_id}`, { responseType: "blob" }); window.open(URL.createObjectURL(res.data), "_blank", "noopener"); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const remove = async (d) => {
    if (!window.confirm(`Remove "${d.file_name}" from ${driver.first_name}'s file? The attachment is deleted from the request too.`)) return;
    try { const { data } = await api.delete(`/drivers/${driver.id}/report-documents/${d.id}`); toast.success("Attachment removed"); onChanged?.(data); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <div className="mb-4 rounded-md border border-emerald-200 bg-emerald-50 p-3" data-testid="driver-report-docs">
      <div className="tag-mono text-emerald-800 mb-2">REPORTS ON FILE · PSP / MVR · included in DQ binder exhibits</div>
      <ul className="space-y-1">
        {docs.map((d) => (
          <li key={d.id} className="flex items-center justify-between gap-2 text-xs">
            <button type="button" onClick={() => open(d)} className="text-blue-700 hover:underline inline-flex items-center gap-1 min-w-0" data-testid={`driver-report-doc-${d.id}`}><FileText className="h-3.5 w-3.5 shrink-0" /><span className="font-medium">{d.doc_type}</span><span className="truncate text-slate-500">· {d.file_name}</span></button>
            <span className="inline-flex items-center gap-2 shrink-0"><span className="font-mono text-slate-500">{(d.uploaded_at || "").slice(0, 10)}</span>{isAdmin && <button type="button" onClick={() => remove(d)} className="text-slate-400 hover:text-rose-600" title="Remove attachment" data-testid={`driver-report-doc-delete-${d.id}`}><Trash2 className="h-3.5 w-3.5" /></button>}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function TsaLetterRow({ driver, onChanged }) {
  const [busy, setBusy] = useState(false);
  const doc = driver.tsa_letter;
  const upload = async (file) => {
    if (!file) return; setBusy(true);
    try { const fd = new FormData(); fd.append("file", file); const { data } = await api.post(`/drivers/${driver.id}/tsa-letter`, fd, { headers: { "Content-Type": "multipart/form-data" } }); onChanged(data); toast.success("TSA letter attached — it will appear in the hazmat audit binder"); }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  const open = async () => { try { const res = await api.get(`/drivers/${driver.id}/tsa-letter/file`, { responseType: "blob" }); window.open(URL.createObjectURL(res.data), "_blank", "noopener"); } catch (err) { toast.error(formatApiError(err)); } };
  const remove = async () => { if (!window.confirm("Remove the TSA letter file?")) return; await api.delete(`/drivers/${driver.id}/tsa-letter`); onChanged({ ...driver, tsa_letter: null }); };
  return (
    <div className="flex items-center justify-between gap-2 flex-wrap" data-testid="driver-tsa-letter">
      <div className="text-xs">{doc ? <button type="button" onClick={open} className="text-blue-700 hover:underline inline-flex items-center gap-1" data-testid="driver-tsa-letter-open"><FileText className="h-3.5 w-3.5" />{doc.file_name}</button> : <span className="text-slate-400">No TSA determination letter on file</span>}</div>
      <div className="flex items-center gap-2">
        <label className="inline-flex items-center gap-1 text-xs text-blue-700 cursor-pointer hover:underline" data-testid="driver-tsa-letter-upload"><Upload className="h-3.5 w-3.5" />{busy ? "Uploading…" : doc ? "Replace letter" : "Attach TSA letter"}<input type="file" accept="application/pdf,image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} /></label>
        <ScanButton onFile={upload} disabled={busy} testid="driver-tsa-letter-scan" />
        {doc && <button type="button" onClick={remove} data-testid="driver-tsa-letter-remove"><Trash2 className="h-3.5 w-3.5 text-rose-600" /></button>}
      </div>
    </div>
  );
}

function TrainingCertRow({ driver, onChanged }) {
  const [busy, setBusy] = useState(false);
  const cert = driver.hazmat_training_cert;
  const nextDue = driver.hazmat_training_date ? driver.hazmat_training_date.replace(/^(\d{4})/, (y) => String(Number(y) + 3)) : null;
  const upload = async (file) => {
    if (!file) return; setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/drivers/${driver.id}/hazmat-training`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChanged(data.driver);
      toast.success(data.detected ? `Certificate attached — training date ${data.training_date} read from the certificate` : `Certificate attached — training date set to ${data.training_date} (edit the driver if the certificate shows a different date)`);
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  const open = async () => { try { const res = await api.get(`/drivers/${driver.id}/hazmat-training/file`, { responseType: "blob" }); const url = URL.createObjectURL(res.data); window.open(url, "_blank", "noopener"); } catch (err) { toast.error(formatApiError(err)); } };
  const remove = async () => { if (!window.confirm("Remove the training certificate file? The training date stays.")) return; await api.delete(`/drivers/${driver.id}/hazmat-training`); onChanged({ ...driver, hazmat_training_cert: null }); };
  return (
    <div className="col-span-2 rounded-md border border-orange-200 bg-orange-50/40 p-3" data-testid="driver-training-cert">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="tag-mono text-slate-500">Hazmat training (49 CFR 172.704)</div>
          <div className="font-medium text-slate-900">{driver.hazmat_training_date ? <>Trained {driver.hazmat_training_date} · next due {nextDue}</> : "No training date on file"}</div>
          {cert && <button type="button" onClick={open} className="text-xs text-blue-700 hover:underline inline-flex items-center gap-1 mt-1" data-testid="driver-training-cert-open"><FileText className="h-3.5 w-3.5" />{cert.file_name}<span className="text-slate-400">· {new Date(cert.uploaded_at).toLocaleDateString()}{cert.date_detected ? " · date read from certificate" : ""}</span></button>}
        </div>
        <div className="flex items-center gap-2">
          <label className="inline-flex items-center gap-1 text-sm text-orange-800 border border-orange-300 bg-white rounded-md px-3 py-1.5 cursor-pointer hover:bg-orange-50" data-testid="driver-training-cert-upload"><Upload className="h-4 w-4" />{busy ? "Uploading…" : cert ? "Replace certificate" : "Attach certificate"}<input type="file" accept="application/pdf,image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} /></label>
          {cert && <Button variant="ghost" size="icon" onClick={remove} data-testid="driver-training-cert-remove"><Trash2 className="h-4 w-4 text-rose-600" /></Button>}
          <ScanButton onFile={upload} disabled={busy} testid="driver-training-cert-scan" />
        </div>
      </div>
      <p className="text-[11px] text-slate-500 mt-2">Attach the signed certificate; we read the completion date from PDF certificates automatically and set the next recurrent training 3 years out.</p>
    </div>
  );
}
